import { Sha256, bytesToHex } from './sha256';

export type FileHashResult = { hashHex: string | null; supported: boolean };

function hasFileSlice(file: File): boolean {
  return typeof file.slice === 'function' && typeof file.size === 'number';
}

async function readBlobAsArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  const anyBlob = blob as unknown as { arrayBuffer?: () => Promise<ArrayBuffer> };
  if (typeof anyBlob.arrayBuffer === 'function') {
    return anyBlob.arrayBuffer();
  }

  if (typeof FileReader === 'undefined') {
    throw new Error('FileReader not available');
  }

  return new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read blob'));
    reader.readAsArrayBuffer(blob);
  });
}

function createHashWorker(): Worker {
  return new Worker(new URL('../workers/hash-worker.ts', import.meta.url), { type: 'module' });
}

export async function calculateFileHash(
  file: File,
  onProgress?: (progress: number) => void
): Promise<FileHashResult> {
  if (typeof file === 'undefined' || !hasFileSlice(file)) {
    return { hashHex: null, supported: false };
  }

  if (typeof Worker !== 'undefined') {
    return new Promise<FileHashResult>((resolve) => {
      const worker = createHashWorker();

      const cleanup = () => {
        worker.terminate();
      };

      worker.onmessage = (event: MessageEvent) => {
        const data = event.data as { type: string; progress?: number; hashHex?: string; message?: string };
        if (data.type === 'progress' && typeof data.progress === 'number') {
          onProgress?.(data.progress);
          return;
        }
        if (data.type === 'result' && typeof data.hashHex === 'string') {
          cleanup();
          resolve({ hashHex: data.hashHex, supported: true });
          return;
        }
        if (data.type === 'error') {
          cleanup();
          resolve({ hashHex: null, supported: true });
        }
      };

      worker.onerror = () => {
        cleanup();
        resolve({ hashHex: null, supported: true });
      };

      worker.postMessage({ type: 'hash', file });
    });
  }

  // Fallback: compute on main thread (still incremental, but not off-thread).
  try {
    const sha = new Sha256();
    const total = file.size;
    const chunkSize = 1024 * 1024;
    let processed = 0;

    for (let offset = 0; offset < total; offset += chunkSize) {
      const end = Math.min(offset + chunkSize, total);
      const buf = await readBlobAsArrayBuffer(file.slice(offset, end));
      sha.update(new Uint8Array(buf));
      processed = end;
      const progress = total > 0 ? Math.floor((processed / total) * 100) : 100;
      onProgress?.(progress);
    }

    return { hashHex: bytesToHex(sha.digest()), supported: true };
  } catch {
    return { hashHex: null, supported: true };
  }
}

export async function calculateRangeProof(file: File, offset: number, length: number): Promise<string> {
  if (offset < 0 || length <= 0 || !hasFileSlice(file)) {
    throw new Error('Invalid range');
  }

  const end = Math.min(offset + length, file.size);
  const buf = await readBlobAsArrayBuffer(file.slice(offset, end));
  const sha = new Sha256();
  sha.update(new Uint8Array(buf));
  return bytesToHex(sha.digest());
}
